import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe } from '@fortawesome/free-solid-svg-icons';

// Components

import SsapHyperDashboard from './hyperDashboards/SsapHyperDashboard';
import MemberHomeHyperDashboard from './hyperDashboards/MemberHomeHyperDashboard';
import Settings from './blocks/Settings';
import HyperDashboardAutocomplete from './HyperDashboardAutocomplete';
import SsapDataInconsistencyBanner from '@components/ssap/dev/SsapDataInconsistencyBanner';

// ActionCreators

import { changeLang } from '@actions/common/actionCreators';

// Hooks

import { useGodMode } from '@hooks/useGodMode';

// Utils

import { localStorageHyperDashboardTools } from '@utils/tools/localStorageHyperDashboardTools';

// Constants

import { configs } from '@constants/configs/configs';
import { localStorageConfigs } from '@configs/localStorageConfigs';

// Styles

import { darculaHyperDashboardStyles } from './darculaTheme';

const {
    hyperDashboardRootStyle,
    routeLabelStyle,
    routeSelectStyle,
    routeAndStateRowStyle,
    routeControlColumnStyle,
    langToolbarRowStyle,
    langToolbarStyle,
    langGlobeIconStyle,
    langBtnBaseStyle,
    langBtnActiveStyle,
    langBtnInactiveStyle,
} = darculaHyperDashboardStyles;

const HyperDashboard = ({ currentLocation }) => {

    const language = useSelector(state => state.common.environment.language);
    const dispatch = useDispatch();

    const isHyperDashboardShown = useGodMode();
    const preferred =  localStorageHyperDashboardTools.getValueByPropName('preferredRoute', 'SSAP');
    const routes = useMemo(
        () => [ ...Object.entries(configs.routes) ].sort(([ a ], [ b ]) => a.localeCompare(b)),
        []
    );

    const routeAutocompleteOptions = useMemo(
        () => routes.map(([ routeName, routePath ]) => ({
            value: routePath,
            label: routeName === preferred ? `${routeName} (preferred)` : routeName,
            optionId: `route-${routeName}`,
        })),
        [ routes, preferred ]
    );

    const [ stateCodeValue, setStateCodeValue ] = useState(() =>
        localStorageHyperDashboardTools.getValueByPropName(
            'stateCode',
            localStorageConfigs.stateCode.value
        )
    );

    const stateCodeOptions = localStorageConfigs.stateCode.options;

    // Controlled <select> value: match URL pathname exactly when it is a known route; 
    // otherwise show preferred (or first) route.
    const selectValue = routes.some(([, path]) => path === currentLocation)
        ? currentLocation
        : (configs.routes[preferred] ?? routes[0]?.[1] ?? '');

    const handleLanguageChange = ({ currentTarget: { dataset } }) => {
        const { url } = dataset;
        if (url) {
            dispatch(changeLang(url));
        }
    };

    const handleStateCodeChange = ({ target: { value } }) => {
        localStorageHyperDashboardTools.setValueByPropName('stateCode', value);
        setStateCodeValue(value);
    };

    useEffect(() => {
        const syncStateCodeFromStorage = () => {
            setStateCodeValue(
                localStorageHyperDashboardTools.getValueByPropName(
                    'stateCode',
                    localStorageConfigs.stateCode.value
                )
            );
        };
        window.addEventListener('hyperDashboardSettingsReset', syncStateCodeFromStorage);
        return () => window.removeEventListener('hyperDashboardSettingsReset', syncStateCodeFromStorage);
    }, []);

    const renderLangBtns = () => (
        <div style={ langToolbarRowStyle }>
            <div style={ langToolbarStyle }>
                <FontAwesomeIcon
                    icon={ faGlobe }
                    title="Language"
                    aria-hidden
                    style={ langGlobeIconStyle }
                />
                {
                    ['en', 'es'].map(l => {
                        const active = l === language;
                        return (
                            <button
                                key={ l }
                                type="button"
                                data-url={ configs.endpoints.CHANGE_LANGUAGE.processReplacements({ language: l }) }
                                onClick={ handleLanguageChange }
                                aria-pressed={ active }
                                aria-label={ `Switch language to ${l.toUpperCase()}` }
                                style={{
                                    ...langBtnBaseStyle,
                                    ...(active ? langBtnActiveStyle : langBtnInactiveStyle),
                                }}
                            >
                                { l.toUpperCase() }
                            </button>
                        );
                    })
                }
            </div>
        </div>
    );

    const renderHyperDashboard = () => {
        switch(currentLocation) {
            case configs.routes.SSAP:
                return <SsapHyperDashboard />;
            case configs.routes.INDPORTAL:
                return <MemberHomeHyperDashboard/>;
        }
    };

    return isHyperDashboardShown ?
        <div style={ hyperDashboardRootStyle }>
            { currentLocation === configs.routes.SSAP && <SsapDataInconsistencyBanner /> }
            { renderLangBtns() }
            <div style={ routeAndStateRowStyle }>
                <div style={ routeControlColumnStyle }>
                    <label
                        htmlFor="hyper-dashboard-state-code"
                        style={ routeLabelStyle }
                    >
                        { localStorageConfigs.stateCode.text }:
                    </label>
                    <select
                        id="hyper-dashboard-state-code"
                        name="stateCode"
                        value={ stateCodeValue }
                        onChange={ handleStateCodeChange }
                        style={ routeSelectStyle }
                    >
                        {
                            stateCodeOptions.map(({ value, text }, idx) => (
                                <option key={ `${value}_${idx}` } value={ value }>
                                    { text }
                                </option>
                            ))
                        }
                    </select>
                </div>
                <div style={ routeControlColumnStyle }>
                    <HyperDashboardAutocomplete
                        id="hyper-dashboard-route"
                        labelText="Route:"
                        options={ routeAutocompleteOptions }
                        value={ selectValue }
                        onValueChange={ routePath => { window.location = routePath; } }
                        placeholder="Type route name or path…"
                    />
                </div>
            </div>
            <span>
                UI build:{ ' ' }
                <span style={{ fontStyle: 'italic' }}>
                    { configs.ENV.DEVELOPMENT ? 'Current UI codebase' : __SNAPSHOT__ }
                </span>
            </span>
            <Settings { ...{ currentLocation } }/>
            { renderHyperDashboard() }
        </div> : null;
};

export default HyperDashboard;
